// src/pages/purchase/CreateDirectPO.js
// ZENO ERP — Direct PO Creation (without MRF)
// Accessible by: SM, Owner, Manager
// Tagged: source: "direct" in Firestore
// Same flow as MRF PO: Draft → Approve → GRN

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs,
  query, where
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { createPurchaseOrder, calculateLandedCost, fetchRecentPOHistory, getItemRateHistory } from "../services/purchase/purchaseOrderService";

// ── Category allowlist (same as PurchaseOrderDetail) ─────────────────────────
const PURCHASE_ALLOWED_CATEGORIES = ["MATERIAL", "CONSUMABLE", "ASSET"];
function isPurchaseCategory(cat) {
  if (!cat) return true;
  return PURCHASE_ALLOWED_CATEGORIES.includes(cat.toUpperCase().trim());
}

function normalizeName(name = "") {
  return name.toLowerCase().trim().replace(/\s+/g, " ");
}

// Same fixed unit list already used in ProjectMaterials.jsx — kept
// consistent so units entered here match units entered there exactly
// (no "Rmt" vs "rmt" vs "RMT" drift across the app).
const UNIT_OPTIONS = ["Nos", "Mtr", "Kg", "Set", "Lot", "LS", "Rmt", "Sqft"];

export default function CreateDirectPO() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // ── Form state ────────────────────────────────────────────────────────────
  const [vendorId,   setVendorId]   = useState("");
  const [vendorName, setVendorName] = useState("");
  const [notes,      setNotes]      = useState("");
  const [items,      setItems]      = useState([
    { rowKey: `row_${Date.now()}`, item_id: null, item_name: "", item_category: "",
      unit: "", ordered_qty: "", rate: "", is_custom_item: false,
      discount_type: "percent", discount_value: "", gst_percent: "18" }
  ]);

  // Rate Breakdown — freight is one flat amount for the whole PO (a single
  // shipment cost), split across items proportionally when computing
  // landed cost. Reference only — never feeds GRN's own rate.
  const [freightAmount, setFreightAmount] = useState("");

  // Direct Site Purchase (Priority 4) — defaults to false, every
  // existing Direct PO keeps behaving exactly as before.
  const [isSitePurchase, setIsSitePurchase] = useState(false);
  const [projects,          setProjects]          = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Site Engineers assigned to the selected project (for the optional
  // per-PO pin) — refetched whenever the project selection changes.
  const [projectSiteEngineers, setProjectSiteEngineers] = useState([]);
  const [assignedEngineerIds,  setAssignedEngineerIds]  = useState([]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [vendors,      setVendors]      = useState([]);
  const [itemsMaster,  setItemsMaster]  = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const itemsMasterRef = useRef([]);

  // Lowest-Rate Suggestion — fetched once (like itemsMaster), matched
  // per-item client-side. Reference only, never auto-fills any field.
  const [poHistory,        setPoHistory]        = useState([]);
  const [openHistoryRowKey, setOpenHistoryRowKey] = useState(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const errorRef = useRef(null);
  const [searchState, setSearchState] = useState({});
  // Item-name inputs, keyed by rowKey — used to measure each input's
  // on-screen position so the suggestion dropdown (position: fixed) can
  // line up under it, independent of the table's own horizontal scroll.
  const itemNameInputRefs = useRef({});

  // Close any open suggestion dropdown on scroll (capture phase catches
  // scroll events from the table's own overflow-x-auto wrapper, which
  // don't bubble to window otherwise) — simpler and more robust than
  // repositioning a fixed-position dropdown on every scroll tick.
  useEffect(() => {
    const closeAllSuggestions = () => {
      setSearchState(prev => {
        const next = {};
        for (const key in prev) next[key] = { ...prev[key], open: false };
        return next;
      });
    };
    window.addEventListener("scroll", closeAllSuggestions, true);
    return () => window.removeEventListener("scroll", closeAllSuggestions, true);
  }, []);

  // ── Load vendors ──────────────────────────────────────────────────────────
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "vendors"),
          where("company_id", "==", currentUser.company_id || "")
        ));
        setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(v => v.status !== "inactive")
          .sort((a, b) => (a.vendor_name || a.name || "").localeCompare(b.vendor_name || b.name || "")));
      } catch (e) {
        console.error("[CreateDirectPO] loadVendors:", e);
      }
    };
    if (currentUser?.company_id) loadVendors();
  }, [currentUser?.company_id]);

  // ── Load projects (Direct Site Purchase only) ───────────────────────────
  // Site-linked projects only — standing pattern (CORE_RULES §14/§29).
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "projects"),
          where("company_id", "==", currentUser.company_id || "")
        ));
        setProjects(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(p => p.site_id)
            .sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""))
        );
      } catch (e) {
        console.error("[CreateDirectPO] loadProjects:", e);
      }
    };
    if (currentUser?.company_id) loadProjects();
  }, [currentUser?.company_id]);

  // ── Load ALL company Site Engineers whenever a project is selected —
  // not just those already assigned to it. This lets a Manager pin (and
  // auto-assign, on save) an engineer to a Direct Site Purchase right
  // here, without a separate trip to the project's Edit modal. Engineers
  // already on the project are tagged for clarity; anyone else picked
  // gets added to the project's site_engineer_ids[] at PO-save time.
  useEffect(() => {
    const loadCompanyEngineers = async () => {
      setAssignedEngineerIds([]);
      if (!selectedProjectId || !currentUser?.company_id) {
        setProjectSiteEngineers([]);
        return;
      }
      try {
        const snap = await getDocs(query(
          collection(db, "users"),
          where("company_id", "==", currentUser.company_id || ""),
          where("role", "==", "site_engineer")
        ));
        setProjectSiteEngineers(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
      } catch (e) {
        console.error("[CreateDirectPO] loadCompanyEngineers:", e);
      }
    };
    loadCompanyEngineers();
  }, [selectedProjectId, currentUser?.company_id]);

  // ── Load items master ─────────────────────────────────────────────────────
  useEffect(() => {
    const loadItems = async () => {
      try {
        setMasterLoading(true);
        const snap = await getDocs(query(collection(db, "items"), where("company_id", "==", currentUser.company_id || "")));
        const raw = [];
        snap.forEach(d => {
          const item = { id: d.id, ...d.data() };
          const itemName = item.item_name?.trim() || item.name?.trim() || "";
          if (!itemName) return;
          item.item_name = itemName;
          const cat = item.item_category || item.category || null;
          if (!isPurchaseCategory(cat)) return;
          if (!item.item_category && item.category) item.item_category = item.category;
          raw.push(item);
        });
        // Dedup by name
        const nameMap = new Map();
        for (const item of raw) {
          const key = normalizeName(item.item_name);
          if (!nameMap.has(key)) nameMap.set(key, item);
          else if (!nameMap.get(key).company_id && item.company_id === currentUser.company_id)
            nameMap.set(key, item);
        }
        const deduped = Array.from(nameMap.values()).sort((a, b) => a.item_name.localeCompare(b.item_name));
        itemsMasterRef.current = deduped;
        setItemsMaster(deduped);
      } catch (e) {
        console.error("[CreateDirectPO] loadItems:", e);
      } finally {
        setMasterLoading(false);
      }
    };
    if (currentUser?.company_id) loadItems();
  }, [currentUser?.company_id]);

  // ── Load rate history (Lowest-Rate Suggestion) ────────────────────────────
  useEffect(() => {
    const loadHistory = async () => {
      const history = await fetchRecentPOHistory(currentUser.company_id || "");
      setPoHistory(history);
    };
    if (currentUser?.company_id) loadHistory();
  }, [currentUser?.company_id]);

  // ── Item search ───────────────────────────────────────────────────────────
  const getSuggestions = useCallback((queryStr, rowKey) => {
    if (!queryStr?.trim() || queryStr.trim().length < 2) return [];
    const q = normalizeName(queryStr);
    const master = itemsMasterRef.current;
    const inPONames = new Set(
      items.filter(r => r.item_name?.trim()).map(r => normalizeName(r.item_name))
    );
    const thisRow = items.find(r => r.rowKey === rowKey);
    const thisName = thisRow ? normalizeName(thisRow.item_name || "") : "";
    return master
      .filter(m => normalizeName(m.item_name).includes(q))
      .map(m => ({ ...m, _alreadyAdded: inPONames.has(normalizeName(m.item_name)) && normalizeName(m.item_name) !== thisName }))
      .slice(0, 10);
  }, [items]);

  const handleSearchChange = useCallback((rowKey, value) => {
    const suggestions = getSuggestions(value, rowKey);
    const isCustom = value.trim().length >= 2 && suggestions.length === 0;
    setItems(prev => prev.map(r => r.rowKey === rowKey
      ? { ...r, item_name: value, item_id: null, is_custom_item: isCustom }
      : r
    ));
    // Measure the input's on-screen position so the fixed-position dropdown
    // (rendered below) lines up under it regardless of the table's own
    // horizontal scroll offset.
    const inputEl = itemNameInputRefs.current[rowKey];
    const rect = inputEl?.getBoundingClientRect();
    setSearchState(prev => ({
      ...prev,
      [rowKey]: {
        query: value, suggestions, open: suggestions.length > 0,
        pos: rect ? { top: rect.bottom, left: rect.left, width: rect.width } : prev[rowKey]?.pos,
      },
    }));
  }, [getSuggestions]);

  const handleSelectSuggestion = useCallback((rowKey, master) => {
    if (master._alreadyAdded) return;
    setItems(prev => prev.map(r => r.rowKey === rowKey
      ? { ...r, item_id: master.item_id || "", item_name: master.item_name, item_category: master.item_category || "", unit: master.unit || "", is_custom_item: false }
      : r
    ));
    setSearchState(prev => ({ ...prev, [rowKey]: { query: master.item_name, suggestions: [], open: false } }));
  }, []);

  // ── Item row management ───────────────────────────────────────────────────
  const addRow = () => {
    const rowKey = `row_${Date.now()}`;
    setItems(prev => [...prev, { rowKey, item_id: null, item_name: "", item_category: "", unit: "", ordered_qty: "", rate: "", is_custom_item: false, discount_type: "percent", discount_value: "", gst_percent: "18" }]);
    setSearchState(prev => ({ ...prev, [rowKey]: { query: "", suggestions: [], open: false } }));
  };

  const removeRow = (rowKey) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter(r => r.rowKey !== rowKey));
    setSearchState(prev => { const s = { ...prev }; delete s[rowKey]; return s; });
  };

  const updateRow = (rowKey, field, value) => {
    setItems(prev => prev.map(r => r.rowKey === rowKey ? { ...r, [field]: value } : r));
  };

  // ── Grand total ───────────────────────────────────────────────────────────
  const grandTotal = items.reduce((sum, item) => {
    return sum + (Number(item.ordered_qty || 0) * Number(item.rate || 0));
  }, 0);

  // ── Rate Breakdown preview (reference only — never affects grandTotal
  // above, which stays exactly as before). Uses the SAME calculation the
  // backend applies at creation (calculateLandedCost), so what the
  // Manager sees here is guaranteed identical to what gets stored.
  const landedItems = calculateLandedCost(
    items.map(item => ({
      ...item,
      qty: Number(item.ordered_qty || 0),
      base_rate: Number(item.rate || 0),
    })),
    Number(freightAmount || 0)
  );
  const landedGrandTotal = landedItems.reduce((sum, item) => sum + (item.landed_total || 0), 0);

  // ── PO number generation ──────────────────────────────────────────────────
  // REMOVED (Single-Procurement-Engine consolidation) — this used to be a
  // local counter here, duplicating the same logic (and the same D-261/
  // D-262 unscoped-query bug class) already in purchaseOrderService.js's
  // own generatePONumber(). createPurchaseOrder() below now generates the
  // PO number centrally — do NOT reintroduce a second counter here.

  // ── Save PO ───────────────────────────────────────────────────────────────
  // CONSOLIDATED (Single-Procurement-Engine fix) — calls the SAME
  // createPurchaseOrder() service function VendorAssignmentModal.js uses,
  // instead of its own inline addDoc(). PO-numbering and custom-item ID
  // generation both now live in exactly one place.
  const handleSave = async () => {
    setError(null);

    const showError = (msg) => {
      setError(msg);
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    };

    // Validation
    if (!vendorId) { showError("Please select a vendor."); return; }
    if (isSitePurchase && !selectedProjectId) {
      showError("Please select a project for this Direct Site Purchase.");
      return;
    }
    const activeItems = items.filter(i => i.item_name?.trim());
    if (activeItems.length === 0) { showError("Add at least one item."); return; }
    for (const item of activeItems) {
      if (!item.ordered_qty || Number(item.ordered_qty) <= 0) {
        showError(`Enter qty for "${item.item_name}".`); return;
      }
      // Rate can be 0 at creation — vendor quote may come later
      // Rate will be filled before approval or during amendment
    }

    try {
      setSaving(true);
      const vendor = vendors.find(v => v.id === vendorId);

      const payloadItems = activeItems.map((item) => ({
        item_id:        item.item_id || null, // null → service generates a custom_ id
        item_name:      item.item_name.trim(),
        item_category:  item.item_category || "",
        unit:           item.unit || "",
        ordered_qty:    Number(item.ordered_qty),
        rate:           Number(item.rate),
        is_custom_item: item.is_custom_item || !item.item_id,
        // Rate Breakdown — reference only, never overrides `rate` above or
        // GRN's own rate entry.
        discount_type:  item.discount_type === "flat" ? "flat" : "percent",
        discount_value: Number(item.discount_value || 0),
        gst_percent:    Number(item.gst_percent || 0),
      }));

      const selectedProject = projects.find(p => p.id === selectedProjectId);

      // If any newly-picked Site Engineer isn't already on the project,
      // add them to the project's own site_engineer_ids[] too — so this
      // isn't just a one-off PO-level pin, it's a real project
      // assignment the Manager can see/manage from the project itself
      // afterward, and future POs on this project benefit from it too.
      if (isSitePurchase && selectedProject && assignedEngineerIds.length > 0) {
        const existing = selectedProject.site_engineer_ids || [];
        const newIds = assignedEngineerIds.filter(id => !existing.includes(id));
        if (newIds.length > 0) {
          try {
            const { updateDoc, doc: firestoreDoc } = await import("firebase/firestore");
            await updateDoc(firestoreDoc(db, "projects", selectedProjectId), {
              site_engineer_ids: [...existing, ...newIds],
            });
          } catch (e) {
            console.error("[CreateDirectPO] auto-assign to project failed (non-blocking):", e);
          }
        }
      }

      const response = await createPurchaseOrder({
        company_id:       currentUser.company_id || "",
        vendor_id:        vendorId,
        vendor_name:      vendor?.vendor_name || vendor?.name || vendorName,
        vendor_email:     vendor?.email || "",
        vendor_phone:     vendor?.phone || "",
        vendor_address:   vendor?.address || "",
        vendor_gst:       vendor?.gst_number || "",
        source:           "direct",         // ← tagged as Direct PO, same as before
        purchase_mode:    isSitePurchase ? "direct_site_purchase" : "stock_purchase",
        project_id:       isSitePurchase ? selectedProjectId : "",
        project_name:     isSitePurchase ? (selectedProject?.project_name || "") : "",
        site_id:          isSitePurchase ? (selectedProject?.site_id || "") : "",
        site_name:        isSitePurchase ? (selectedProject?.site_name || "") : "",
        assigned_site_engineer_ids: isSitePurchase ? assignedEngineerIds : [],
        notes:            notes.trim(),
        created_by:       currentUser.uid,
        created_by_name:  currentUser.name || currentUser.email || "",
        freight_amount:   Number(freightAmount || 0),
        items:            payloadItems,
      });

      if (!response.success) {
        throw response.error || new Error("Purchase order creation failed");
      }

      console.log("[CreateDirectPO] PO created:", response.po_number, response.po_id);
      navigate(`/purchase-orders/${response.po_id}`);

    } catch (e) {
      console.error("[CreateDirectPO] Error:", e);
      setError("Failed to create PO. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 max-w-[1750px] mx-auto">

      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1">
          ← Back
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">Create Direct Purchase Order</h1>
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Direct PO</span>
        </div>
        <p className="text-sm text-gray-500 mt-1">Create a PO directly without MRF. Status will be Draft — requires approval before GRN.</p>
      </div>

      {error && (
        <div ref={errorRef} className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          ⚠ {error}
        </div>
      )}

      {/* Vendor Selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Vendor</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Select Vendor *</label>
            <select
              value={vendorId}
              onChange={e => {
                setVendorId(e.target.value);
                const v = vendors.find(v => v.id === e.target.value);
                setVendorName(v?.vendor_name || v?.name || "");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none"
            >
              <option value="">— Select Vendor —</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.vendor_name || v.name}</option>
              ))}
            </select>
          </div>
          {vendorId && (() => {
            const v = vendors.find(v => v.id === vendorId);
            return v ? (
              <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm">
                <p className="font-medium text-gray-800">{v.vendor_name || v.name}</p>
                {v.contact_person && <p className="text-gray-500 text-xs">Attn: {v.contact_person}</p>}
                {v.phone && <p className="text-gray-500 text-xs">📞 {v.phone}</p>}
                {v.email && <p className="text-gray-500 text-xs">📧 {v.email}</p>}
                {v.gst_number && <p className="text-gray-500 text-xs">GST: {v.gst_number}</p>}
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Direct Site Purchase (Priority 4). Off (default): unchanged —
          received at Store. On: Site Engineer confirms qty, Manager/SM/
          Owner finalizes with rate — see ConfirmSiteReceipt.js /
          FinalizeSitePurchase.js. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Delivery</h2>
        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={isSitePurchase}
            onChange={e => {
              setIsSitePurchase(e.target.checked);
              if (!e.target.checked) setSelectedProjectId("");
            }}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium text-gray-700">
            This is a Direct Site Purchase — vendor delivers straight to a project site, not to Store
          </span>
        </label>

        {isSitePurchase && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Select Project *</label>
            <select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none"
            >
              <option value="">— Select Project —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.project_name} — {p.site_name}</option>
              ))}
            </select>
            {projects.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No site-linked projects found.</p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              Site Engineers assigned to this project will confirm received quantity; a Manager/SM/Owner will then finalize rate and vendor payment.
            </p>

            {selectedProjectId && (() => {
              const selectedProject = projects.find(p => p.id === selectedProjectId);
              const projectAssignedIds = selectedProject?.site_engineer_ids || [];
              return (
                <div className="mt-4">
                  <label className="block text-xs text-gray-500 mb-1">
                    Pin to specific Site Engineer(s) — optional
                  </label>
                  {projectSiteEngineers.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      No Site Engineer users exist in your company yet — add one via User Management first.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-1.5 border border-gray-200 rounded-lg p-2 max-w-sm max-h-40 overflow-y-auto">
                        {projectSiteEngineers.map(se => {
                          const checked   = assignedEngineerIds.includes(se.id);
                          const onProject = projectAssignedIds.includes(se.id);
                          return (
                            <label key={se.id} className="flex items-center gap-2 text-sm">
                              <input type="checkbox" checked={checked}
                                onChange={e => setAssignedEngineerIds(prev =>
                                  e.target.checked ? [...prev, se.id] : prev.filter(id => id !== se.id)
                                )} />
                              {se.name || se.email || se.id}
                              {onProject && (
                                <span className="text-[10px] text-green-600 font-medium">✓ on project</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Anyone picked here who isn't already on this project will be added to it automatically. Leave unchecked to let anyone assigned to the project confirm it.
                      </p>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Items — single-line-per-item table. Horizontal scroll (inside the
          card only) on narrow screens instead of wrapping fields onto a
          second row, so the whole breakdown (name → amount) always reads
          as one row. Card itself never widens — width matches every other
          card on this page. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">Items</h2>
          {!masterLoading && itemsMaster.length > 0 && (
            <span className="text-xs text-green-600">✓ {itemsMaster.length} items in catalogue</span>
          )}
          {masterLoading && <span className="text-xs text-blue-500">Loading catalogue...</span>}
        </div>

        {/* overflow-x-auto is back — without it, a wide table (many
            columns) silently bled past the card's edge instead of
            scrolling. The item-search suggestion dropdown below is
            rendered with position: fixed and JS-computed coordinates
            instead of position: absolute, so CSS overflow clipping on
            this wrapper never hides it (overflow only clips fixed-position
            descendants when the ancestor has its own transform/filter/
            contain, which this element doesn't). */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-xs text-gray-500 uppercase">
                <th className="text-left font-medium pb-3 pr-3 min-w-[340px]">Item Name *</th>
                <th className="text-left font-medium pb-3 px-3 w-24">Unit</th>
                <th className="text-right font-medium pb-3 px-3 w-32">Qty *</th>
                <th className="text-right font-medium pb-3 px-3 w-36">Rate (₹) *</th>
                <th className="text-center font-medium pb-3 px-3 w-56">Discount</th>
                <th className="text-right font-medium pb-3 px-3 w-28">GST %</th>
                <th className="text-right font-medium pb-3 px-3 w-32">Landed Rate</th>
                <th className="text-right font-medium pb-3 px-3 w-36">Landed Total</th>
                <th className="text-right font-medium pb-3 px-3 w-28">Amount</th>
                <th className="pb-3 pl-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const rs = searchState[item.rowKey] || { query: item.item_name || "", suggestions: [], open: false };
                const rowTotal = Number(item.ordered_qty || 0) * Number(item.rate || 0);
                return (
                  <tr key={item.rowKey} className="border-t border-gray-100 align-top">
                    {/* Item name search */}
                    <td className="py-2.5 pr-3 relative">
                      <input
                        ref={el => { itemNameInputRefs.current[item.rowKey] = el; }}
                        type="text"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck="false"
                        value={rs.query || item.item_name || ""}
                        onChange={e => handleSearchChange(item.rowKey, e.target.value)}
                        placeholder="Type to search items..."
                        title={rs.query || item.item_name || ""}
                        className="w-full min-w-[340px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none bg-white"
                      />
                      {rs.open && rs.pos && (
                        <div
                          style={{ position: "fixed", top: rs.pos.top + 4, left: rs.pos.left, width: Math.max(rs.pos.width, 280) }}
                          className="bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                          {rs.suggestions.map(s => (
                            <div key={s.id} onClick={() => !s._alreadyAdded && handleSelectSuggestion(item.rowKey, s)}
                              className={`px-3 py-2 text-sm ${s._alreadyAdded ? "bg-gray-50 cursor-not-allowed text-gray-400" : "hover:bg-indigo-50 cursor-pointer text-gray-800"}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{s.item_name}</span>
                                {s._alreadyAdded
                                  ? <span className="text-xs text-orange-500">Already added</span>
                                  : <span className="text-xs text-gray-400">{s.unit || ""}</span>
                                }
                              </div>
                              {s.item_category && <p className="text-xs text-gray-400">{s.item_category}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {item.is_custom_item && item.item_name && (
                        <p className="text-xs text-purple-500 mt-0.5 whitespace-nowrap">⚡ Custom</p>
                      )}
                      {/* Lowest-Rate Suggestion — reference only, never
                          auto-fills Rate/Discount/GST above. Shown only
                          when past PO history exists for this item. */}
                      {item.item_name?.trim() && (() => {
                        const history = getItemRateHistory(poHistory, item);
                        if (history.length === 0) return null;
                        const isOpen = openHistoryRowKey === item.rowKey;
                        return (
                          <div className="mt-1">
                            <button type="button"
                              onClick={() => setOpenHistoryRowKey(isOpen ? null : item.rowKey)}
                              className="text-xs text-teal-600 hover:text-teal-800 font-medium whitespace-nowrap">
                              📊 {history.length} past rate{history.length > 1 ? "s" : ""} — from ₹{history[0].landed_rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </button>
                            {isOpen && (
                              <div className="mt-1 border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1 min-w-[280px]">
                                {history.map((h, i) => (
                                  <div key={i} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${i === 0 ? "bg-teal-50 font-semibold text-teal-800" : "text-gray-600"}`}>
                                    <span className="truncate">
                                      {h.vendor_name} · {h.created_at?.toDate ? h.created_at.toDate().toLocaleDateString("en-IN") : "—"}
                                      {i === 0 && <span className="ml-1 text-teal-600">(lowest)</span>}
                                    </span>
                                    <span className="whitespace-nowrap ml-2">₹{h.landed_rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>

                    {/* Unit — fixed dropdown (same list as ProjectMaterials.jsx)
                        instead of free text, so units never drift ("Rmt" vs
                        "rmt" vs "RMT"). If a value arrived from Item Master
                        that isn't in the standard list, it's kept as an extra
                        option rather than silently dropped. */}
                    <td className="py-2.5 px-3">
                      <select value={item.unit || ""} onChange={e => updateRow(item.rowKey, "unit", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none bg-white ${
                          item.unit ? "border-gray-300 text-gray-800" : "border-amber-300 text-gray-400"
                        }`}>
                        <option value="" disabled>Select unit</option>
                        {(item.unit && !UNIT_OPTIONS.includes(item.unit) ? [item.unit, ...UNIT_OPTIONS] : UNIT_OPTIONS)
                          .map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>

                    {/* Qty — sized for a 5-digit value (up to 99999) */}
                    <td className="py-2.5 px-3">
                      <input type="number" min="0.01" step="0.01" value={item.ordered_qty || ""} onChange={e => updateRow(item.rowKey, "ordered_qty", e.target.value)}
                        placeholder="0" className="w-full min-w-[120px] px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-indigo-300 outline-none bg-white" />
                    </td>

                    {/* Rate — sized for a 7-digit value (up to 9999999) */}
                    <td className="py-2.5 px-3">
                      <input type="number" min="0.01" step="0.01" value={item.rate || ""} onChange={e => updateRow(item.rowKey, "rate", e.target.value)}
                        placeholder="0" className="w-full min-w-[160px] px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-indigo-300 outline-none bg-white" />
                    </td>

                    {/* Discount — width follows the mode: % tops out at 100
                        (3 digits) so stays narrow; flat ₹ can realistically
                        run into 6 digits (subtotal-sized), so widens the
                        moment the dropdown switches to ₹. max=100 for %
                        stays, so a mistyped value can never silently blow
                        up the landed-cost calculation */}
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1.5">
                        <select
                          value={item.discount_type || "percent"}
                          onChange={e => updateRow(item.rowKey, "discount_type", e.target.value)}
                          className="px-2 py-2 border border-gray-300 rounded-lg text-sm outline-none bg-white shrink-0">
                          <option value="percent">%</option>
                          <option value="flat">₹</option>
                        </select>
                        <input type="number" min="0"
                          max={item.discount_type === "flat" ? undefined : 100}
                          step="0.01" value={item.discount_value || ""}
                          onChange={e => updateRow(item.rowKey, "discount_value", e.target.value)}
                          placeholder="0"
                          className={`px-3 py-2 border border-gray-300 rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-indigo-300 bg-white ${
                            item.discount_type === "flat" ? "w-[140px]" : "w-[90px]"
                          }`} />
                      </div>
                    </td>

                    {/* GST % — max=100 as a sanity ceiling (real GST slabs
                        top out at 28%; 100 just guards against a mistyped
                        value silently multiplying the landed cost, which
                        is exactly what happened when this box was too
                        narrow to show what was actually typed) */}
                    <td className="py-2.5 px-3">
                      <input type="number" min="0" max="100" step="0.01" value={item.gst_percent ?? ""}
                        onChange={e => updateRow(item.rowKey, "gst_percent", e.target.value)}
                        placeholder="18"
                        className="w-full min-w-[80px] px-3 py-2 border border-gray-300 rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-indigo-300 bg-white" />
                    </td>

                    {/* Landed Rate — reference only */}
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-teal-700 whitespace-nowrap">
                      ₹{(landedItems[idx]?.landed_rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </td>

                    {/* Landed Total — reference only */}
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-teal-700 whitespace-nowrap">
                      ₹{(landedItems[idx]?.landed_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </td>

                    {/* Amount */}
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                      ₹{rowTotal.toLocaleString("en-IN")}
                    </td>

                    {/* Remove */}
                    <td className="py-2.5 pl-3 text-right">
                      {items.length > 1 && (
                        <button onClick={() => removeRow(item.rowKey)} className="text-gray-300 hover:text-red-500 text-xs" title="Remove item">✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <button onClick={addRow} className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
          + Add Item
        </button>
      </div>

      {/* Rate Breakdown — Freight (one flat amount for the whole PO) */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-3">Rate Breakdown — Freight</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Freight / Transport (₹, whole PO)</label>
            <input type="number" min="0" step="0.01" value={freightAmount}
              onChange={e => setFreightAmount(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none" />
            <p className="text-xs text-gray-400 mt-1">Split across items proportionally — reference only, never changes Rate/Amount or the GRN rate.</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Landed Cost (all items, ref. only)</p>
            <p className="text-lg font-bold text-teal-700">
              ₹{landedGrandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-3">Notes (Optional)</h2>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
          placeholder="Delivery instructions, payment terms, or any other notes..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none resize-none" />
      </div>

      {/* Total + Submit */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-sm text-gray-500">Total Order Value</p>
            <p className="text-2xl font-bold text-gray-900">
              ₹{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-right text-xs text-gray-400">
            <p>{items.filter(i => i.item_name?.trim()).length} items</p>
            <p className="mt-0.5">Status: <span className="font-semibold text-gray-600">Draft</span></p>
            <p className="mt-0.5">Source: <span className="font-semibold text-purple-600">Direct PO</span></p>
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={() => navigate(-1)} disabled={saving}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !vendorId}
            className="flex-1 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
            {saving ? "Creating PO..." : "Create Draft PO →"}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-3 text-center">
          PO will be saved as Draft. SM / Owner must approve before GRN is allowed.
        </p>
      </div>
    </div>
  );
}